import { MetaData, FilterCategory } from 'uiComponents/filter/schema'
import { formatFlatOptions } from 'uiComponents/filter/filterHelpers'
import { t } from 'translations'

interface ApplicableFiltersConfig {
    orders: FilterCategory[]
    tickets: FilterCategory[]
}

export const applicableFilters: ApplicableFiltersConfig = {
    orders: ['products', 'payment_method', 'channels', 'language', 'location_id', 'email_status', 'status'],
    tickets: ['products', 'payment_method', 'channels', 'language'],
}

// Labels are getters so they are translated when read, not when this module loads.
export const categoryOptions = {
    payment_method: [
        { name: 'Amex', slug: 'amex' },
        { name: 'Bancontact', slug: 'bancontact' },
        {
            get name() {
                return t('orders.orders.filters.paymentMethod.banktransfer')
            },
            slug: 'banktransfer',
        },
        { name: 'BCMC', slug: 'bcmc' },
        { name: 'Belfius', slug: 'belfius' },
        { name: 'Convious', slug: 'convious' },
        {
            get name() {
                return t('orders.orders.filters.paymentMethod.creditcard')
            },
            slug: 'creditcard',
        },
        { name: 'Direct E-Banking', slug: 'directEbanking' },
        { name: 'EBanking FI', slug: 'ebanking_FI' },
        { name: 'Entercash', slug: 'entercash' },
        { name: 'EPS', slug: 'eps' },
        {
            get name() {
                return t('orders.orders.filters.paymentMethod.giftcard')
            },
            slug: 'giftcard',
        },
        { name: 'Giropay', slug: 'giropay' },
        { name: 'Ideal', slug: 'ideal' },
        { name: 'Inghomepay', slug: 'inghomepay' },
        { name: 'KBC', slug: 'kbc' },
        { name: 'MC', slug: 'mc' },
        { name: 'Paypal', slug: 'paypal' },
        { name: 'Przelewy24', slug: 'przelewy24' },
        { name: 'Sofort', slug: 'sofort' },
        { name: 'Visa', slug: 'visa' },
    ],
    email_status: [
        {
            get name() {
                return t('orders.orders.filters.emailStatus.sending')
            },
            slug: 'sending',
        },
        {
            get name() {
                return t('orders.orders.filters.emailStatus.processed')
            },
            slug: 'processed',
        },
        {
            get name() {
                return t('orders.orders.filters.emailStatus.delivered')
            },
            slug: 'delivered',
        },
        {
            get name() {
                return t('orders.orders.filters.emailStatus.opened')
            },
            slug: 'opened',
        },
        {
            get name() {
                return t('orders.orders.filters.emailStatus.clicked')
            },
            slug: 'clicked',
        },
        {
            get name() {
                return t('orders.orders.filters.emailStatus.deferred')
            },
            slug: 'deferred',
        },
        {
            get name() {
                return t('orders.orders.filters.emailStatus.bounced')
            },
            slug: 'bounced',
        },
        {
            get name() {
                return t('orders.orders.filters.emailStatus.dropped')
            },
            slug: 'dropped',
        },
    ],
    ticket_status: [
        {
            get name() {
                return t('orders.orders.filters.ticketStatus.paid')
            },
            slug: 'paid',
        },
        {
            get name() {
                return t('orders.orders.filters.ticketStatus.refunded')
            },
            slug: 'refunded',
        },
        {
            get name() {
                return t('orders.orders.filters.ticketStatus.chargedBack')
            },
            slug: 'charged_back',
        },
        {
            get name() {
                return t('orders.orders.filters.ticketStatus.redeemed')
            },
            slug: 'redeemed',
        },
    ],
    apf_status: [
        {
            get name() {
                return t('orders.orders.filters.apfStatus.apfCompleted')
            },
            slug: 'apf_completed',
        },
        {
            get name() {
                return t('orders.orders.filters.apfStatus.apfMissing')
            },
            slug: 'apf_missing',
        },
    ],
    redeem_status: [
        {
            get name() {
                return t('orders.orders.filters.redeemStatus.redeemed')
            },
            slug: 'redeemed',
        },
        {
            get name() {
                return t('orders.orders.filters.redeemStatus.notRedeemed')
            },
            slug: 'not_redeemed',
        },
    ],
    print_status: [
        {
            get name() {
                return t('orders.orders.filters.printStatus.printed')
            },
            slug: 'printed',
        },
        {
            get name() {
                return t('orders.orders.filters.printStatus.notPrinted')
            },
            slug: 'not_printed',
        },
    ],
    status: [
        {
            get name() {
                return t('orders.orders.filters.status.confirmed')
            },
            slug: 'CONFIRMED',
        },
        {
            get name() {
                return t('orders.orders.filters.status.canceled')
            },
            slug: 'CANCELED',
        },
        {
            get name() {
                return t('orders.orders.filters.status.open')
            },
            slug: 'OPEN',
        },
        {
            get name() {
                return t('orders.orders.filters.status.redeemed')
            },
            slug: 'REDEEMED',
        },
        {
            get name() {
                return t('orders.orders.filters.status.partiallyRedeemed')
            },
            slug: 'PARTIALLY_REDEEMED',
        },
        {
            get name() {
                return t('orders.orders.filters.status.partiallyCancelled')
            },
            slug: 'PARTIALLY_CANCELLED',
        },
    ],
}

export const getStaticMetadaCategories = (): MetaData[] => [
    {
        category: 'payment_method' as FilterCategory,
        options: formatFlatOptions('payment_method', categoryOptions.payment_method),
    },
    {
        category: 'email_status' as FilterCategory,
        options: formatFlatOptions('email_status', categoryOptions.email_status),
    },
    {
        category: 'ticket_status' as FilterCategory,
        options: formatFlatOptions('ticket_status', categoryOptions.ticket_status),
    },
    {
        category: 'apf_status' as FilterCategory,
        options: formatFlatOptions('apf_status', categoryOptions.apf_status),
    },
    {
        category: 'redeem_status' as FilterCategory,
        options: formatFlatOptions('redeem_status', categoryOptions.redeem_status),
    },
    {
        category: 'print_status' as FilterCategory,
        options: formatFlatOptions('print_status', categoryOptions.print_status),
    },
    {
        category: 'status' as FilterCategory,
        options: formatFlatOptions('status', categoryOptions.status),
    },
]
